import React from 'react'
import { useAuth } from '@clerk/clerk-react'
import { useQuery } from '@tanstack/react-query'
import { Coins, Activity, Wallet, TrendingUp } from 'lucide-react'
import { StatCard } from '@/components/dashboard/StatCard'
import { UsageChart } from '@/components/charts/UsageChart'
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card'
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell, TableWrapper } from '@/components/ui/table'
import { Badge } from '@/components/ui/badge'
import { getSupabaseWithClerkAuth } from '@/lib/supabase'
import { formatCurrency, formatTokens } from '@/lib/currency'
import type { DailyUsage, UsageEvent } from '@/lib/supabase'

export default function DashboardOverview() {
  const { getToken } = useAuth()

  const { data: customer } = useQuery({
    queryKey: ['customer'],
    queryFn: async () => {
      const db = await getSupabaseWithClerkAuth(getToken)
      const { data } = await db.from('customers').select('*').single()
      return data
    },
  })

  const currency = customer?.currency_preference || 'PHP'

  const { data: dailyUsage = [] } = useQuery<DailyUsage[]>({
    queryKey: ['daily-usage', 7],
    queryFn: async () => {
      const db = await getSupabaseWithClerkAuth(getToken)
      const { data } = await db
        .from('customer_usage_daily')
        .select('*')
        .order('day', { ascending: true })
        .limit(7)
      return data || []
    },
  })

  const { data: recentEvents = [] } = useQuery<UsageEvent[]>({
    queryKey: ['recent-events'],
    queryFn: async () => {
      const db = await getSupabaseWithClerkAuth(getToken)
      const { data } = await db
        .from('usage_events')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(10)
      return data || []
    },
  })

  // Compute stats
  const totalTokens = dailyUsage.reduce((sum, d) => sum + d.total_tokens, 0)
  const todayUsage = dailyUsage[dailyUsage.length - 1]
  const monthCost = dailyUsage.reduce((sum, d) => sum + d.total_cost_usd, 0)
  const balance = customer?.credit_balance_usd ?? 10.00

  function formatTime(iso: string): string {
    return new Date(iso).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
        <StatCard
          title="Total Tokens (7 days)"
          value={formatTokens(totalTokens)}
          subtitle="Across all models"
          icon={Coins}
          iconColor="text-indigo-600"
          iconBg="bg-indigo-50"
          trend={{ value: 12.5, label: 'vs last week' }}
        />
        <StatCard
          title="Requests Today"
          value={(todayUsage?.request_count ?? 0).toLocaleString()}
          subtitle="API calls"
          icon={Activity}
          iconColor="text-violet-600"
          iconBg="bg-violet-50"
          trend={{ value: 8.3, label: 'vs yesterday' }}
        />
        <StatCard
          title="Current Balance"
          value={formatCurrency(balance, currency)}
          subtitle={`≈ $${balance.toFixed(2)} USD`}
          icon={Wallet}
          iconColor="text-green-600"
          iconBg="bg-green-50"
        />
        <StatCard
          title="This Month's Cost"
          value={formatCurrency(monthCost, currency)}
          subtitle={`≈ $${monthCost.toFixed(4)} USD`}
          icon={TrendingUp}
          iconColor="text-orange-600"
          iconBg="bg-orange-50"
          trend={{ value: -4.2, label: 'vs last month' }}
        />
      </div>

      {/* Chart */}
      <Card>
        <CardHeader>
          <CardTitle>Token Usage — Last 7 Days</CardTitle>
        </CardHeader>
        <CardContent>
          <UsageChart data={dailyUsage} height={220} />
        </CardContent>
      </Card>

      {/* Recent requests */}
      <div>
        <h2 className="text-base font-semibold text-gray-900 mb-3">Recent Requests</h2>
        <TableWrapper>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Model</TableHead>
                <TableHead>Tier</TableHead>
                <TableHead>Tokens</TableHead>
                <TableHead>Cost</TableHead>
                <TableHead>Time</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {recentEvents.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} className="text-center py-10 text-gray-400">
                    No requests yet — make your first API call to see activity here.
                  </TableCell>
                </TableRow>
              ) : (
                recentEvents.map((event) => (
                  <TableRow key={event.id}>
                    <TableCell className="font-mono text-xs">{event.model}</TableCell>
                    <TableCell>
                      <Badge variant={event.tier as 'speed' | 'standard' | 'premium' | 'media'}>
                        {event.tier}
                      </Badge>
                    </TableCell>
                    <TableCell>{formatTokens(event.total_tokens)}</TableCell>
                    <TableCell>{formatCurrency(event.cost_usd, currency)}</TableCell>
                    <TableCell className="text-gray-500">{formatTime(event.created_at)}</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableWrapper>
      </div>
    </div>
  )
}
